<template>
  <div
    v-if="report"
    class="fp-sheet"
  >
    <b-alert
      v-if="isUnbalanced"
      show
      variant="danger"
    >
      {{ $t('ledger_fp_unbalanced', { difference: formatAmount(report.totals.difference) }) }}
    </b-alert>
    <b-alert
      v-if="hasPriorYears"
      show
      variant="warning"
    >
      {{ $t('ledger_fp_prior_banner', { amount: formatAmount(priorLine.amount) }) }}
      <b-link :to="{ name: 'ledger-year-closings' }">
        {{ $t('ledger_year_title') }}
      </b-link>
    </b-alert>

    <div class="fp-sides">
      <section
        v-for="side in sides"
        :key="side.key"
        class="fp-side"
      >
        <h5 class="fp-side-title">
          {{ labelOf(side) }}
        </h5>
        <div class="fp-side-main">
          <table class="fp-table">
            <colgroup>
              <col :style="{ width: labelCol }">
              <col :style="{ width: amountCol }">
              <col :style="{ width: amountCol }">
              <col
                v-if="hasComparative"
                :style="{ width: amountCol }"
              >
              <col
                v-if="hasComparative"
                :style="{ width: amountCol }"
              >
              <col
                v-if="hasComparative"
                :style="{ width: amountCol }"
              >
            </colgroup>
            <thead>
              <tr>
                <th>{{ $t('ledger_fp_item') }}</th>
                <th
                  class="fp-num"
                  colspan="2"
                >
                  {{ hasComparative ? asOfLabel : $t('ledger_fp_amount') }}
                </th>
                <th
                  v-if="hasComparative"
                  class="fp-num"
                  colspan="2"
                >
                  {{ compareLabel }}
                </th>
                <th
                  v-if="hasComparative"
                  class="fp-num"
                >
                  {{ $t('ledger_fp_change') }}
                </th>
              </tr>
            </thead>
            <tbody
              v-for="(section, sectionIndex) in side.sections || []"
              :key="section.key || sectionIndex"
              class="fp-section"
            >
              <tr class="fp-section-title">
                <td :colspan="columnCount">
                  {{ labelOf(section) }}
                </td>
              </tr>
              <tr
                v-for="row in sectionRows(side, section)"
                :key="row.id"
                :class="{ 'fp-account': row.kind === 'account' }"
              >
                <template v-if="row.kind === 'line'">
                  <td>
                    <button
                      v-if="canExpand(row.line)"
                      type="button"
                      class="fp-chevron"
                      @click="toggle(row.id)"
                    >
                      <feather-icon
                        :icon="isOpen(row.id) ? 'ChevronDownIcon' : closedChevron"
                        size="14"
                      />
                    </button>
                    <b-link
                      v-if="row.line.key === 'net_profit'"
                      v-b-tooltip.hover
                      :to="profitRoute"
                      :title="profitTip"
                    >
                      {{ labelOf(row.line) }}
                    </b-link>
                    <span v-else>{{ labelOf(row.line) }}</span>
                    <b-link
                      v-if="row.line.key === 'prior_years_result' && isNonZero(row.line.amount)"
                      v-b-tooltip.hover
                      :to="{ name: 'ledger-year-closings' }"
                      :title="$t('ledger_fp_prior_tip')"
                      class="fp-prior-icon"
                    >
                      <feather-icon
                        icon="AlertTriangleIcon"
                        size="14"
                        class="text-warning"
                      />
                    </b-link>
                  </td>
                  <td class="fp-num">
                    {{ formatAmount(row.line.amount) }}
                  </td>
                  <td />
                  <template v-if="hasComparative">
                    <td class="fp-num">
                      {{ formatComparative(row.line.comparative) }}
                    </td>
                    <td />
                    <td class="fp-num">
                      {{ formatComparative(delta(row.line.amount, row.line.comparative)) }}
                    </td>
                  </template>
                </template>
                <template v-else>
                  <td class="fp-account-label">
                    {{ row.account.code }} — {{ accountName(row.account) }}
                    <b-badge
                      v-if="row.account.is_active === false"
                      variant="secondary"
                      class="fp-inactive"
                    >
                      {{ $t('ledger_inactive') }}
                    </b-badge>
                  </td>
                  <td class="fp-num">
                    {{ formatAmount(row.account.amount) }}
                  </td>
                  <td />
                  <template v-if="hasComparative">
                    <td class="fp-num">
                      {{ formatComparative(row.account.comparative) }}
                    </td>
                    <td />
                    <td class="fp-num">
                      {{ formatComparative(delta(row.account.amount, row.account.comparative)) }}
                    </td>
                  </template>
                </template>
              </tr>
              <tr class="fp-section-total">
                <td>{{ totalLabel(section) }}</td>
                <td />
                <td class="fp-num">
                  {{ formatAmount(section.total) }}
                </td>
                <template v-if="hasComparative">
                  <td />
                  <td class="fp-num">
                    {{ formatComparative(section.comparative_total) }}
                  </td>
                  <td class="fp-num">
                    {{ formatComparative(delta(section.total, section.comparative_total)) }}
                  </td>
                </template>
              </tr>
            </tbody>
          </table>
        </div>
        <table class="fp-table fp-grand-table">
          <colgroup>
            <col :style="{ width: labelCol }">
            <col :style="{ width: amountCol }">
            <col :style="{ width: amountCol }">
            <col
              v-if="hasComparative"
              :style="{ width: amountCol }"
            >
            <col
              v-if="hasComparative"
              :style="{ width: amountCol }"
            >
            <col
              v-if="hasComparative"
              :style="{ width: amountCol }"
            >
          </colgroup>
          <tbody>
            <tr>
              <td>
                {{ grandLabel(side) }}
              </td>
              <td />
              <td class="fp-num">
                {{ formatAmount(side.total) }}
              </td>
              <template v-if="hasComparative">
                <td />
                <td class="fp-num">
                  {{ formatComparative(side.comparative_total) }}
                </td>
                <td class="fp-num">
                  {{ formatComparative(delta(side.total, side.comparative_total)) }}
                </td>
              </template>
            </tr>
          </tbody>
        </table>
      </section>
    </div>
    <p
      v-if="isBalanced"
      class="fp-balanced"
    >
      {{ $t('ledger_fp_balanced') }}
    </p>
  </div>
</template>

<script>
import {
  BAlert, BLink, BBadge, VBTooltip,
} from 'bootstrap-vue'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'

export default {
  name: 'FinancialPositionSheet',
  components: {
    BAlert, BLink, BBadge, FeatherIcon,
  },
  directives: { 'b-tooltip': VBTooltip },
  props: {
    report: { type: Object, default: null },
    showComparative: { type: Boolean, default: true },
  },
  data() {
    return { openLines: {} }
  },
  computed: {
    sides() {
      const report = this.report || {}
      return [report.assets, report.liabilities_and_equity].filter(side => side && typeof side === 'object')
    },
    hasComparative() {
      return this.showComparative && !!(this.report && this.report.compare_to)
    },
    columnCount() {
      return this.hasComparative ? 6 : 3
    },
    labelCol() {
      return this.hasComparative ? '28%' : '46%'
    },
    amountCol() {
      return this.hasComparative ? '14.4%' : '27%'
    },
    asOfLabel() {
      return this.displayDate(this.report && this.report.as_of) || this.$t('ledger_fp_amount')
    },
    compareLabel() {
      return this.displayDate(this.report && this.report.compare_to)
    },
    closedChevron() {
      return this.$store.state.appConfig.layout.isRTL ? 'ChevronLeftIcon' : 'ChevronRightIcon'
    },
    priorLine() {
      const sides = this.sides
      for (let s = 0; s < sides.length; s += 1) {
        const sections = sides[s].sections || []
        for (let i = 0; i < sections.length; i += 1) {
          const lines = sections[i].lines || []
          for (let n = 0; n < lines.length; n += 1) {
            if (lines[n].key === 'prior_years_result') return lines[n]
          }
        }
      }
      return null
    },
    hasPriorYears() {
      return !!(this.priorLine && this.isNonZero(this.priorLine.amount))
    },
    isBalanced() {
      return !!(this.report && this.report.totals && this.report.totals.balanced === true)
    },
    isUnbalanced() {
      return !!(this.report && this.report.totals && this.report.totals.balanced === false)
    },
    profitRoute() {
      const report = this.report || {}
      const query = {}
      if (report.fiscal_year_start) query.from = report.fiscal_year_start
      if (report.as_of) query.to = report.as_of
      if (report.scope && report.scope.scope === 'company') query.scope = 'company'
      return { name: 'ledger-report-profit', query }
    },
    profitTip() {
      const report = this.report || {}
      return this.$t('ledger_fp_profit_tip', {
        from: this.displayDate(report.fiscal_year_start),
        to: this.displayDate(report.as_of),
      })
    },
  },
  methods: {
    labelOf(row) {
      if (!row) return ''
      if (this.$i18n.locale === 'en') return row.label || row.label_ar || ''
      return row.label_ar || row.label || ''
    },
    totalLabel(section) {
      if (!section) return ''
      if (this.$i18n.locale === 'en') return section.total_label || section.total_label_ar || ''
      return section.total_label_ar || section.total_label || ''
    },
    accountName(account) {
      if (!account) return ''
      if (this.$i18n.locale === 'en') return account.name_en || account.name || ''
      return account.name || account.name_en || ''
    },
    grandLabel(side) {
      if (side && side.key === 'assets') return this.$t('ledger_fp_assets_total')
      if (side && side.key === 'liabilities_and_equity') return this.$t('ledger_fp_liab_total')
      return this.labelOf(side)
    },
    displayDate(iso) {
      const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''))
      if (!match) return ''
      return `${match[3]}/${match[2]}/${match[1]}`
    },
    formatAmount(value) {
      const n = Number(value)
      const rounded = Number.isFinite(n) ? Math.round(n * 100) / 100 : 0
      const loc = this.$i18n.locale === 'en' ? 'en-EG' : 'ar-EG'
      const text = new Intl.NumberFormat(loc, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(Math.abs(rounded))
      return rounded < 0 ? `(${text})` : text
    },
    formatComparative(value) {
      if (value === null || value === undefined || value === '') return '—'
      return this.formatAmount(value)
    },
    delta(amount, comparative) {
      if (comparative === null || comparative === undefined || comparative === '') return null
      const left = Number(amount)
      const right = Number(comparative)
      if (!Number.isFinite(left) || !Number.isFinite(right)) return null
      return Math.round((left - right) * 100) / 100
    },
    isNonZero(value) {
      const n = Number(value)
      return Number.isFinite(n) && Math.round(n * 100) !== 0
    },
    canExpand(line) {
      return line && line.computed !== true && Array.isArray(line.accounts) && line.accounts.length > 0
    },
    lineId(side, section, line, lineIndex) {
      return `${side.key}:${section.key}:${line.key}:${lineIndex}`
    },
    sectionRows(side, section) {
      const rows = []
      ;(section.lines || []).forEach((line, lineIndex) => {
        const id = this.lineId(side, section, line, lineIndex)
        rows.push({ kind: 'line', id, line })
        if (this.canExpand(line) && this.isOpen(id)) {
          ;(line.accounts || []).forEach(account => {
            rows.push({
              kind: 'account',
              id: `${id}:${account.gl_account_id || account.code}`,
              account,
            })
          })
        }
      })
      return rows
    },
    isOpen(id) {
      return !!this.openLines[id]
    },
    toggle(id) {
      this.$set(this.openLines, id, !this.openLines[id])
    },
  },
}
</script>

<style scoped>
.fp-sides {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  align-items: stretch;
}
.fp-side {
  display: flex;
  flex-direction: column;
  min-width: 0;
  border: 1px solid #ebe9f1;
  border-radius: 0.5rem;
  padding: 0.75rem;
}
.fp-side-title {
  font-weight: 700;
  text-align: center;
  margin-bottom: 0.5rem;
}
.fp-side-main {
  flex: 1 1 auto;
}
.fp-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}
.fp-table th,
.fp-table td {
  padding: 0.28rem 0.35rem;
  vertical-align: baseline;
}
.fp-table thead th {
  border-bottom: 1px solid #ebe9f1;
  font-weight: 600;
}
.fp-num {
  text-align: end;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.fp-section-title td {
  font-weight: 700;
  text-decoration: underline;
  padding-top: 0.75rem;
}
.fp-section-total td {
  font-weight: 700;
  border-top: 1px solid #06284d;
}
.fp-grand-table {
  margin-top: auto;
  flex: 0 0 auto;
}
.fp-grand-table td {
  font-weight: 700;
  border-top: 3px double #06284d;
}
.fp-balanced {
  color: #28c76f;
  font-weight: 600;
  margin: 0.75rem 0 0;
}
.fp-chevron {
  border: 0;
  background: transparent;
  padding: 0;
  margin-inline-end: 0.25rem;
  color: inherit;
  line-height: 1;
  vertical-align: middle;
}
.fp-account td {
  font-size: 0.85rem;
  color: #6e6b7b;
}
.fp-account-label {
  padding-inline-start: 1.5rem;
}
.fp-inactive {
  background: #e9ecef;
  color: #6c757d;
  font-weight: 500;
  margin-inline-start: 0.35rem;
}
.fp-prior-icon {
  margin-inline-start: 0.35rem;
}
@media (max-width: 991.98px) {
  .fp-sides { grid-template-columns: 1fr; }
}
@media print {
  .fp-sides { grid-template-columns: 1fr 1fr !important; }
  .fp-account,
  .fp-chevron { display: none !important; }
  .fp-section { break-inside: avoid; page-break-inside: avoid; }
  .fp-table { font-size: 11px; }
  .fp-side { border-color: #ccc; }
}
</style>
